/**
 * POS Cashier Discount Modal
 * Allows applying 10% (or custom) discount with manager override
 */
import { useState } from 'react';
import { X, Percent, Shield, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function POSCashierDiscountModal({
  isOpen,
  onClose,
  subtotal,
  staffId,
  staffName,
  onApplyDiscount
}) {
  const [discountPercent, setDiscountPercent] = useState(10);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [managerInfo, setManagerInfo] = useState(null);

  if (!isOpen) return null;

  const discountAmount = (subtotal * discountPercent / 100).toFixed(2);

  const handlePinInput = (digit) => {
    if (pin.length < 6) {
      const newPin = pin + digit;
      setPin(newPin);
      setError('');
      
      // Auto-submit when 6 digits entered
      if (newPin.length === 6) {
        verifyPin(newPin);
      }
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
    setError('');
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  const verifyPin = async (pinToVerify) => {
    setVerifying(true);
    setError('');
    
    try {
      const response = await api.post('/pos/manager-override/verify', {
        override_pin: pinToVerify,
        action_type: 'discount',
        cashier_id: staffId || 'unknown',
        cashier_name: staffName || 'Cashier',
        discount_percent: discountPercent,
        reason: `${discountPercent}% cashier discount`
      });
      
      if (response.data) {
        setVerified(true);
        setManagerInfo({
          name: response.data.manager_name,
          role: response.data.manager_role
        });
        
        // Apply discount after short delay to show success
        setTimeout(() => {
          onApplyDiscount({
            type: 'cashier',
            percent: discountPercent,
            // Don't include 'amount' for percentage discounts - it's calculated dynamically
            // 'amount' field is only for fixed dollar discounts (e.g., "$5 off")
            approvedBy: response.data.manager_name,
            approvedByRole: response.data.manager_role
          });
          onClose();
        }, 800);
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid PIN';
      setError(message);
      setPin('');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-amber-500 text-white p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Percent className="w-6 h-6" />
            Apply Discount
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {verified ? (
            // Success state
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-green-600 dark:text-green-400 mb-2">
                Discount Approved!
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {discountPercent}% discount applied by {managerInfo?.name}
              </p>
            </div>
          ) : (
            <>
              {/* Discount Amount Preview */}
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 mb-6 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Discount Amount</p>
                <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                  {discountPercent}% = ${discountAmount}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                  Subtotal: ${subtotal.toFixed(2)}
                </p>
              </div>

              {/* Quick Discount Buttons */}
              <div className="grid grid-cols-3 gap-2 mb-6">
                {[5, 10, 15].map(percent => (
                  <button
                    key={percent}
                    type="button"
                    onClick={() => setDiscountPercent(percent)}
                    className={`py-3 rounded-lg font-bold text-lg transition-all ${
                      discountPercent === percent
                        ? 'bg-amber-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {percent}%
                  </button>
                ))}
              </div>

              {/* Manager Override Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="flex items-center gap-2 mb-4 text-gray-700 dark:text-gray-300">
                  <Shield className="w-5 h-5 text-amber-500" />
                  <span className="font-semibold">Manager Override Required</span>
                </div>

                {/* PIN Display */}
                <div className="flex justify-center gap-2 mb-4">
                  {[0, 1, 2, 3, 4, 5].map(i => (
                    <div
                      key={i}
                      className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-2xl font-bold ${
                        pin.length > i
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-600'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {pin.length > i ? '•' : ''}
                    </div>
                  ))}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="flex items-center justify-center gap-2 text-red-600 dark:text-red-400 mb-4">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                {/* PIN Pad */}
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => (
                    <button
                      key={digit}
                      type="button"
                      onClick={() => handlePinInput(digit.toString())}
                      disabled={verifying}
                      className="h-14 text-2xl font-bold rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                    >
                      {digit}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={handleClear}
                    disabled={verifying}
                    className="h-14 text-sm font-semibold rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePinInput('0')}
                    disabled={verifying}
                    className="h-14 text-2xl font-bold rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    0
                  </button>
                  <button
                    type="button"
                    onClick={handleBackspace}
                    disabled={verifying}
                    className="h-14 text-xl rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    ⌫
                  </button>
                </div>

                {verifying && (
                  <div className="text-center mt-4 text-amber-600 dark:text-amber-400">
                    Verifying...
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!verified && (
          <div className="px-6 pb-6">
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
